import {
  Box,
  FormControl,
  Textarea,
  useToast

} from "@chakra-ui/react";
import React, {
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import {
  addSeconds,
  subSeconds,
  isAfter
} from "date-fns";
import { useLocalStorage } from "@gravitywelluk/react-hooks";
import _ from "lodash";

import {
  Conversation,
  Message,
  Topic,
  User
} from "src/api";
import { SolidButton } from "src/components/button";
import { Text } from "src/components/text";
import { useSocket } from "src/hooks/useSocket";
import { useAuthContext } from "src/hooks/useAuthContext";
import { theme } from "src/components/theme";

import { Messages } from "./Messages";
import { SessionTimer } from "./SessionTimer";

interface FormProps {
  message: string
}
interface ChatProps {
  conversation: Conversation;
  topic: Topic;
  userId: string;
}

interface ConversationCache {
  messages?: Message[];
  conversationId?: string;
}
export const Chat: React.FC<ChatProps> = props => {
  const { topic, conversation } = props;
  const toast = useToast();
  const [ socket ] = useSocket(topic._id);
  const [ messages, setMessages ] = useState<Message[]>([]);
  const { authDataState } = useAuthContext();
  const [ messageCache, setMessageCache ] = useLocalStorage<ConversationCache>(`message-cache-${props.userId}`, {});
  const [ partner, setPartner ] = useState<User | null>(null);
  const [ isDisabled, setIsChatDisabled ] = useState(false);
  const [ contentLength, setContentLength ] = useState(0);
  const [ hasRenderedMessages, setHasRenderedMessages ] = React.useState(false);

  const loadMessagesFromCache = useCallback(() => {
    if (topic.messages) {
      if (messageCache.messages && messageCache.conversationId === conversation._id) {
        const messages = _.uniqBy(_.concat(topic.messages, messageCache.messages), m => `${m.userId}${m.senderTimeCreated}`);

        setMessages(messages);
      } else {
        setMessages(topic.messages);
      }
    }

    setHasRenderedMessages(true);
  }, [
    conversation._id,
    messageCache.conversationId,
    messageCache.messages,
    topic.messages
  ]);

  // User has switched back to the tab
  const onFocus = () => {
    if (hasRenderedMessages) {
      loadMessagesFromCache();
    }
  };

  useEffect(() => {
    window.addEventListener("focus", onFocus);

    // Specify how to clean up after this effect:
    return () => {
      window.removeEventListener("focus", onFocus);
    };
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setIsChatDisabled(isAfter(new Date(), subSeconds(new Date(conversation.endTime), conversation.latencySeconds)));
    }, 1000);

    return () => clearInterval(interval);
  }, [ conversation ]);

  useEffect(() => {
    if (!hasRenderedMessages) {
      loadMessagesFromCache();
    }
  }, [ hasRenderedMessages, loadMessagesFromCache ]);

  useEffect(() => {
    if (hasRenderedMessages) {
      setMessageCache({
        messages,
        conversationId: conversation._id
      });
    }
  }, [
    messages,
    hasRenderedMessages,
    setMessageCache,
    conversation._id
  ]);

  /** Every time a message is received, push to array of messages */
  useEffect(() => {
    socket?.on("all:message:receive", function (msg: Message) {
      // Only push messages which aren't sent by our current user. They are added at time of sending.

      setMessages(oldMessages => {
        const messageExists = _.find(oldMessages, m => {
          return `${m.userId}${m.senderTimeCreated}` === `${msg.userId}${msg.senderTimeCreated}`;
        });

        if (messageExists) {
          return oldMessages;
        } else {
          return ([ ...oldMessages, msg ]);
        }
      });
    });

    socket?.on("topic:participants", function (users: User[]) {
      setPartner(null);

      users.forEach(user => {
        if (user._id !== authDataState.user?._id) {
          // In V1 we are only using 1 partner, so can just set a single partner
          setPartner(user);
        }
      });
    });
  }, [ socket, authDataState.user?._id ]);

  const {
    register, handleSubmit, setValue
  } = useForm<FormProps>();

  const onSubmit = React.useCallback((data: FormProps) => {
    if (data.message && socket?.connected && conversation && authDataState.user?._id) {
      const message: Omit<Message, "serverTimeCreated"> = {
        userId: authDataState.user?._id,
        content: data.message,
        topicId: topic._id,
        senderTimeCreated: new Date().toISOString()
      };

      setMessages(oldMessages => ([
        ...oldMessages,
        {
          ...message,
          senderTimeCreated: new Date(message.senderTimeCreated).toISOString(),
          // We can mock this response as we know it will arrive after now + latency
          serverTimeCreated: addSeconds(new Date(), conversation.latencySeconds).toISOString()
        }
      ]));
      socket?.emit("message:create", message);
      setValue("message", "");
      setContentLength(0);
    } else {
      toast({
        title: "Message failed to send",
        description: "The connection has dropped out. Are you connected to the Internet?",
        status: "error"
      });
    }
  }, [
    conversation,
    setValue,
    authDataState.user?._id,
    socket,
    topic,
    toast
  ]);

  const handleKeyDown = React.useCallback((event: React.KeyboardEvent) => {
    const textLength = (event?.target as HTMLInputElement).value?.length;

    if (event.key === " ") {
      if (textLength >= 500) {
        onSubmit({ message: (event?.target as HTMLInputElement).value });
      }
    }

    setContentLength(textLength);
  }, [ onSubmit ]);

  const handleBlur = React.useCallback((event: React.ChangeEvent) => {
    setContentLength((event?.target as HTMLInputElement).value?.length);
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <SessionTimer
        conversation={conversation}
        partner={partner}
      />

      <Messages
        messages={messages}
        conversation={conversation}
      />

      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          display="flex"
          flexDirection="row"
        >
          <FormControl id="message">

            <Textarea
              id="chat_input"
              readOnly={isDisabled}
              onKeyDown={handleKeyDown}
              {...register("message")}
              onBlur={handleBlur}
            />

            <Box
              mt={2}
              color={contentLength > 500 ? theme.colors.error : contentLength > 450 ? theme.colors.warn : theme.colors.grey}
            >
              <Text as="small">
                {`${500 - contentLength} characters left`}
              </Text>
            </Box>
          </FormControl>

          <SolidButton
            disabled={isDisabled || contentLength > 500}
            marginLeft={5}
            type="submit"
          >
            Send
          </SolidButton>
        </Box>
      </form>

    </Box>
  );
};
